/**
 * Show Idea Generation Service
 *
 * Creates a queue job for show idea generation and returns the job ID.
 * The caller should use useQueueJob hook to poll for job completion.
 *
 * @module showIdeaService
 */

import type { StandardResponse } from '@shared/types';
import { logger } from '@musedrops/shared';
import { getEdgeFunctionAuthHeaders } from './supabase';

interface GenerateShowIdeasRequest {
  genreId: string;
  genreName: string;
  genreDescription: string;
  ideasCount: number;
}

interface QueueJobResponse {
  jobId: number;
  genreId: string;
  message: string;
}

/**
 * Create a queue job for show ideas generation.
 * Returns job ID immediately - use useQueueJob hook to poll for completion.
 *
 * @example
 * ```tsx
 * const { jobId } = await generateShowIdeas(params);
 * watchJob(jobId); // From useQueueJob hook
 * // When job completes, output.shows contains the generated ideas
 * ```
 */
export const generateShowIdeas = async (
  params: GenerateShowIdeasRequest
): Promise<StandardResponse<QueueJobResponse>> => {
  try {
    const ideasCount =
      Number.isFinite(params.ideasCount) && params.ideasCount > 0
        ? Math.floor(params.ideasCount)
        : 5;

    // Call the gen-show-ideas edge function - creates queue job
    const response = await fetch(
      `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/gen-show-ideas`,
      {
        method: 'POST',
        headers: await getEdgeFunctionAuthHeaders(),
        body: JSON.stringify({
          genreId: params.genreId,
          genreName: params.genreName,
          genreDescription: params.genreDescription,
          ideasCount,
          trigger: 'manual', // UI-initiated jobs get priority
        }),
      }
    );

    const data = await response.json();

    // Handle HTTP errors
    if (!response.ok) {
      logger.error('Generate show ideas HTTP error', {
        status: response.status,
        data,
      });
      return {
        success: false,
        error: data.error || 'API Error',
      };
    }

    // Edge function returns { success, result: { jobId, genreId, message } }
    if (!data.success || !data.result?.jobId) {
      return {
        success: false,
        error: data.error || 'Failed to create queue job',
      };
    }

    return {
      success: true,
      result: data.result,
    };
  } catch (error) {
    logger.error('Generate show ideas failed', { error });

    if (error instanceof Error) {
      return {
        success: false,
        error: `Network Error: ${error.message}`,
      };
    }

    return {
      success: false,
      error: 'An unexpected error occurred',
    };
  }
};
